/**
 * PostList - 通用帖子列表组件
 * 用于 Square、Subscriptions、MyCloudClippings 页面
 * 支持按博主分组显示、排序
 * 通过 renderPostCard 支持自定义 PostCard 渲染
 */

import React, { ReactNode, useState } from 'react';
import { Empty, Tag } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { CloudPost, CloudBlogger } from '../types';
import {
  SortBy,
  formatCount,
  groupPostsByBlogger,
  sortBloggerGroups,
  sortPageClipPosts,
} from '../utils/postList';

// Blogger 区块头部渲染器类型
export interface BloggerHeaderProps {
  blogger: CloudBlogger;
  onBloggerClick?: (blogger: CloudBlogger) => void;
  onEditTags?: (blogger: CloudBlogger) => void;
  showEditButton?: boolean;
}

// PostCard 渲染器类型
export interface PostCardRenderProps {
  post: CloudPost;
  isPageClip?: boolean;
  isSelected?: boolean;
}

// 自定义分组函数类型
export type GroupByFn = (posts: CloudPost[]) => Record<string, CloudPost[]>;

interface PostListProps {
  posts: CloudPost[];
  sortBy: SortBy;
  emptyText?: string;
  // 自定义 PostCard 渲染（用于不同场景的 PostCard props）
  renderPostCard: (props: PostCardRenderProps) => ReactNode;
  // 可选：自定义博主头部（默认使用内置）
  renderBloggerHeader?: (props: BloggerHeaderProps) => ReactNode;
  // 可选：自定义分组函数（默认使用 groupPostsByBlogger）
  groupBy?: GroupByFn;
  // 是否对单独剪藏区排序（本地剪藏簿需要支持置顶）
  sortPageClips?: boolean;
  // 博主点击事件（用于打开主页）
  onBloggerClick?: (blogger: CloudBlogger) => void;
  // 编辑博主标签事件
  onEditBloggerTags?: (blogger: CloudBlogger) => void;
  // 是否显示编辑按钮（hover 时显示）
  showBloggerEditButton?: boolean;
  // 判断帖子是否被选中（用于框选功能）
  isSelected?: (postId: string) => boolean;
}

// 默认博主header渲染
const DefaultBloggerHeader: React.FC<BloggerHeaderProps> = ({ blogger, onBloggerClick, onEditTags, showEditButton }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isIconHovered, setIsIconHovered] = useState(false);
  
  return (
    <div 
      style={{
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {blogger.avatarUrl && (
        <img
          src={blogger.avatarUrl}
          alt={blogger.nickname || ''}
          style={{ width: 40, height: 40, borderRadius: '50%' }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h5
          style={{
            margin: 0,
            cursor: blogger.profileUrl ? 'pointer' : 'default',
            color: 'var(--ant-color-text)'
          }}
          onClick={() => onBloggerClick?.(blogger)}
        >
          {blogger.nickname || '未知博主'}
        </h5>
        {blogger.followersCount !== undefined && (
          <span style={{ 
            fontWeight: 'normal', 
            fontSize: 14, 
            color: 'var(--ant-color-text-tertiary)', 
            marginLeft: 12 
          }}>
            粉丝 {formatCount(blogger.followersCount)}
          </span>
        )}
      </div>
      {(blogger.tags && blogger.tags.length > 0 || showEditButton) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {blogger.tags && blogger.tags.map((tag: any) => (
            <Tag key={tag.id}>{tag.name}</Tag>
          ))}
          {showEditButton && (
            <button
              className="icon-button"
              style={{
                opacity: isHovered ? 1 : 0,
                color: isIconHovered ? 'var(--ant-color-text)' : 'var(--ant-color-text-tertiary)',
                transition: 'opacity 0.2s, color 0.2s',
              }}
              onMouseEnter={() => setIsIconHovered(true)}
              onMouseLeave={() => setIsIconHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                onEditTags?.(blogger);
              }}
              title="编辑标签"
            >
              <EditOutlined />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PostList: React.FC<PostListProps> = ({
  posts,
  sortBy,
  emptyText = '暂无帖子数据',
  renderPostCard,
  renderBloggerHeader,
  groupBy,
  sortPageClips = false,
  onBloggerClick,
  onEditBloggerTags,
  showBloggerEditButton = false,
  isSelected,
}) => {
  if (posts.length === 0) {
    return <Empty description={emptyText} style={{ padding: '48px 0' }} />;
  }

  // 使用自定义分组函数或默认分组
  const grouped = groupBy ? groupBy(posts) : groupPostsByBlogger(posts);
  const sortedBloggerIds = sortBloggerGroups(Object.keys(grouped), grouped, sortBy);

  const HeaderComponent = renderBloggerHeader 
    ? (props: BloggerHeaderProps) => <>{renderBloggerHeader(props)}</>
    : DefaultBloggerHeader;

  return (
    <>
      {sortedBloggerIds.map(bloggerId => {
        let bloggerPosts = grouped[bloggerId];
        
        // 单独剪藏（无博主信息）
        if (bloggerId === 'pageclip') {
          // 可选排序（支持置顶）
          if (sortPageClips) {
            bloggerPosts = sortPageClipPosts(bloggerPosts);
          }
          
          return (
            <div key="pageclip" style={{ marginBottom: 32 }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: 'var(--ant-color-text)' }}>单独剪藏</h3>
              </div>
              <div className="blogger-posts-grid">
                {bloggerPosts.map(post => (
                  <div key={post.id} data-post-id={post.id}>
                    {renderPostCard({
                      post,
                      isPageClip: true,
                      isSelected: isSelected?.(post.id)
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // 博主分组
        const blogger = bloggerPosts[0]?.blogger;
        if (!blogger) return null;

        return (
          <div key={bloggerId} style={{ marginBottom: 32 }}>
            <HeaderComponent
              blogger={blogger}
              onBloggerClick={onBloggerClick}
              onEditTags={onEditBloggerTags}
              showEditButton={showBloggerEditButton}
            />
            <div className="blogger-posts-grid">
              {bloggerPosts.map(post => (
                <div key={post.id} data-post-id={post.id}>
                  {renderPostCard({
                    post,
                    isPageClip: false,
                    isSelected: isSelected?.(post.id)
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default PostList;
